import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { safeParseJson } from '../lib/storage-utils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Heart, 
  Users, 
  ArrowRight, 
  CheckCircle2, 
  Sparkles,
  Calendar,
  TrendingUp,
  Brain,
  Info,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function RelationshipTransition() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [compatibilityProfile, setCompatibilityProfile] = useState<any>(null);
  const [dateJournal, setDateJournal] = useState<any[]>([]);
  const [missingSetup, setMissingSetup] = useState(false);
  
  // Transition data
  const [partnerName, setPartnerName] = useState('');
  const [partnerFromJournal, setPartnerFromJournal] = useState<any>(null);
  const [relationshipStart, setRelationshipStart] = useState(new Date().toISOString().split('T')[0]);
  const [keepSinglesData, setKeepSinglesData] = useState(true);
  const [importInsights, setImportInsights] = useState(true);

  useEffect(() => {
    const profileRaw = localStorage.getItem('tension_compatibility_profile');
    const journalRaw = localStorage.getItem('tension_date_journal');

    if (!profileRaw) {
      setMissingSetup(true);
      return;
    }

    const profile = safeParseJson<unknown>(profileRaw, null);
    if (profile != null) setCompatibilityProfile(profile);
    if (journalRaw) {
      const journal = safeParseJson<unknown[]>(journalRaw, []);
      setDateJournal(Array.isArray(journal) ? journal : []);
    }
  }, [navigate]);

  useEffect(() => {
    if (missingSetup) {
      toast.error('No singles profile found');
    }
  }, [missingSetup]);

  const uniquePeople = Array.from(new Set(dateJournal.map(e => e.personName)));

  const selectPartnerFromJournal = (name: string) => {
    const entries = dateJournal.filter(e => e.personName === name);
    const mostRecent = entries.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )[0];
    
    setPartnerFromJournal({
      name,
      entries,
      mostRecent,
      stats: {
        totalDates: entries.length,
        avgConnection: (entries.reduce((sum, e) => sum + e.connectionLevel, 0) / entries.length).toFixed(1),
        avgConversation: (entries.reduce((sum, e) => sum + e.conversationQuality, 0) / entries.length).toFixed(1),
        greenFlags: Array.from(new Set(entries.flatMap(e => e.greenFlags))).filter(f => f),
        redFlags: Array.from(new Set(entries.flatMap(e => e.redFlags))).filter(f => f),
      }
    });
    setPartnerName(name);
  };

  const completeTransition = () => {
    if (!partnerName.trim()) {
      toast.error('Please enter your partner\'s name');
      return;
    }

    // Create relationship entry
    const relationshipId = `rel_${Date.now()}`;
    const relationship = {
      id: relationshipId,
      partnerName: partnerName.trim(),
      startDate: relationshipStart,
      isActive: true,
      transitionedFromSingles: true,
      createdAt: new Date().toISOString(),
    };

    const relationshipsRaw = safeParseJson<unknown[]>(localStorage.getItem('tension_relationships'), []);
    const relationships = Array.isArray(relationshipsRaw) ? [...relationshipsRaw] : [];
    relationships.push(relationship);
    localStorage.setItem('tension_relationships', JSON.stringify(relationships));
    localStorage.setItem('tension_active_relationship', relationshipId);

    // Import insights if selected
    if (importInsights && partnerFromJournal) {
      const transitionInsights = {
        singlesProfileSummary: {
          attachmentStyle: compatibilityProfile.patterns.attachmentStyle,
          coreValues: compatibilityProfile.values.coreValues,
          communicationStyle: compatibilityProfile.communication.style,
        },
        datingJournalInsights: {
          totalDates: partnerFromJournal.stats.totalDates,
          avgConnection: partnerFromJournal.stats.avgConnection,
          greenFlagsObserved: partnerFromJournal.stats.greenFlags,
          redFlagsToWatch: partnerFromJournal.stats.redFlags,
          firstDate: partnerFromJournal.entries[partnerFromJournal.entries.length - 1]?.date,
        },
        recommendations: generateRelationshipRecommendations(),
      };

      localStorage.setItem(
        `tension_transition_insights_${relationshipId}`,
        JSON.stringify(transitionInsights)
      );
    }

    // Keep or archive singles data
    if (keepSinglesData) {
      const archive = {
        profile: compatibilityProfile,
        journal: dateJournal,
        transitionDate: new Date().toISOString(),
        transitionedTo: relationshipId,
      };
      localStorage.setItem('tension_singles_archive', JSON.stringify(archive));
    }

    // Update mode
    localStorage.setItem('tension_mode', 'relationship');
    localStorage.setItem('tension_onboarding_complete', 'true');

    // Clear singles mode if not keeping data
    if (!keepSinglesData) {
      localStorage.removeItem('tension_compatibility_profile');
      localStorage.removeItem('tension_compatibility_insights');
      localStorage.removeItem('tension_date_journal');
      localStorage.removeItem('tension_daily_prompt_responses');
    }

    toast.success(`Welcome to relationship mode with ${partnerName}! 🎉`);
    navigate('/home');
  };

  const generateRelationshipRecommendations = () => {
    const recommendations = [];

    // Based on attachment style
    if (compatibilityProfile.patterns.attachmentStyle === 'anxious') {
      recommendations.push('Practice self-soothing when feeling anxious about the relationship');
      recommendations.push('Communicate your need for reassurance clearly and early');
    } else if (compatibilityProfile.patterns.attachmentStyle === 'avoidant') {
      recommendations.push('Work on staying present during moments of emotional intimacy');
      recommendations.push('Communicate when you need space before pulling away');
    }

    // Based on communication style
    if (compatibilityProfile.communication.style === 'direct') {
      recommendations.push('Remember that your partner may need time to process before responding');
    } else if (compatibilityProfile.communication.style === 'subtle') {
      recommendations.push('Practice being more explicit about your needs and feelings');
    }

    // Based on date journal red flags
    if (partnerFromJournal?.stats.redFlags.length > 0) {
      recommendations.push('Keep monitoring the red flags you identified during early connections');
    }

    return recommendations;
  };

  if (!compatibilityProfile) {
    if (missingSetup) {
      return (
        <div className="min-h-screen tension-page-bg flex items-center justify-center px-6">
          <Card className="max-w-xl p-6 space-y-4 text-center">
            <h2 className="text-xl font-medium text-neutral-900">No singles profile to transition yet</h2>
            <p className="text-sm text-neutral-600">
              Relationship Transition imports your singles context. Complete onboarding or compatibility profile first.
            </p>
            <div className="flex justify-center gap-3">
              <Button onClick={() => navigate('/singles-onboarding')}>Go to onboarding</Button>
              <Button variant="outline" onClick={() => navigate('/compatibility-profile')}>Open profile</Button>
            </div>
          </Card>
        </div>
      );
    }
    return (
      <div className="min-h-screen tension-page-bg flex items-center justify-center">
        <p className="text-neutral-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-8">
        <div className="max-w-4xl mx-auto text-center space-y-3">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-light">Congratulations! 🎉</h1>
          <p className="text-lg text-violet-100">
            You're transitioning from singles mode to relationship mode
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {step === 1 && (
          <div className="space-y-6">
            {/* Info Card */}
            <Card className="p-6 bg-blue-50 border-blue-200">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-2">What happens during this transition?</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Your singles data can be preserved for future reference</li>
                    <li>Singles insights will be imported into your new relationship profile</li>
                    <li>You'll start tracking relationship tension with your partner</li>
                    <li>You can always access your singles archive in settings</li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* Partner Selection */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-violet-600" />
                <div>
                  <h2 className="text-lg font-medium">Who are you in a relationship with?</h2>
                  <p className="text-sm text-neutral-600">Select from your singles journal or enter a name</p>
                </div>
              </div>

              {/* From Singles Journal */}
              {uniquePeople.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-neutral-700">From Your Singles Journal:</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {uniquePeople.map(person => {
                      const entries = dateJournal.filter(e => e.personName === person);
                      const avgConnection = (entries.reduce((sum, e) => sum + e.connectionLevel, 0) / entries.length).toFixed(1);
                      
                      return (
                        <button
                          key={person}
                          onClick={() => selectPartnerFromJournal(person)}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            partnerName === person
                              ? 'border-violet-600 bg-violet-50'
                              : 'border-neutral-200 hover:border-neutral-300'
                          }`}
                        >
                          <div className="font-medium text-neutral-900 mb-2">{person}</div>
                          <div className="flex items-center gap-4 text-xs text-neutral-600">
                            <span>{entries.length} date{entries.length !== 1 ? 's' : ''}</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" />
                              {avgConnection}/10
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-neutral-300" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-neutral-50 px-2 text-neutral-500">OR</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Entry */}
              <div>
                <label className="text-sm font-medium text-neutral-700 block mb-2">
                  Enter Partner's Name
                </label>
                <Input
                  value={partnerFromJournal ? '' : partnerName}
                  onChange={(e) => {
                    setPartnerName(e.target.value);
                    setPartnerFromJournal(null);
                  }}
                  placeholder="e.g., Alex"
                  disabled={!!partnerFromJournal}
                />
              </div>

              {/* Relationship Start Date */}
              <div>
                <label className="text-sm font-medium text-neutral-700 block mb-2">
                  When did your relationship officially start?
                </label>
                <Input
                  type="date"
                  value={relationshipStart}
                  onChange={(e) => setRelationshipStart(e.target.value)}
                />
              </div>
            </Card>

            {/* Selected Partner Insights */}
            {partnerFromJournal && (
              <Card className="p-6 space-y-4 bg-violet-50 border-violet-200">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                  <h3 className="font-medium text-violet-900">Singles Insights for {partnerFromJournal.name}</h3>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-2xl font-bold text-violet-600">{partnerFromJournal.stats.totalDates}</div>
                    <div className="text-xs text-neutral-600">Total Dates</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{partnerFromJournal.stats.avgConnection}</div>
                    <div className="text-xs text-neutral-600">Avg Connection</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{partnerFromJournal.stats.avgConversation}</div>
                    <div className="text-xs text-neutral-600">Avg Conversation</div>
                  </div>
                </div>

                {partnerFromJournal.stats.greenFlags.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Green Flags You Observed ({partnerFromJournal.stats.greenFlags.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {partnerFromJournal.stats.greenFlags.slice(0, 5).map((flag: string, idx: number) => (
                        <Badge key={idx} className="bg-green-100 text-green-800 border-green-200 text-xs">
                          {flag}
                        </Badge>
                      ))}
                      {partnerFromJournal.stats.greenFlags.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{partnerFromJournal.stats.greenFlags.length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {partnerFromJournal.stats.redFlags.length > 0 && (
                  <div className="tension-glow bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-yellow-800 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Red Flags to Monitor ({partnerFromJournal.stats.redFlags.length})
                    </p>
                    <p className="text-xs text-yellow-700">
                      These patterns were noted during early interactions. Keep an eye on them as your relationship develops.
                    </p>
                  </div>
                )}
              </Card>
            )}

            {/* Next Button */}
            <Button
              onClick={() => setStep(2)}
              disabled={!partnerName.trim()}
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              size="lg"
            >
              Continue
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            {/* Data Preferences */}
            <Card className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Brain className="w-6 h-6 text-violet-600" />
                <div>
                  <h2 className="text-lg font-medium">Data & Insights</h2>
                  <p className="text-sm text-neutral-600">Choose what to preserve and import</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Keep Singles Data */}
                <button
                  onClick={() => setKeepSinglesData(!keepSinglesData)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    keepSinglesData
                      ? 'border-violet-600 bg-violet-50'
                      : 'border-neutral-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      keepSinglesData
                        ? 'border-violet-600 bg-violet-600'
                        : 'border-neutral-300'
                    }`}>
                      {keepSinglesData && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">Keep Singles Mode Archive</p>
                      <p className="text-sm text-neutral-600 mt-1">
                        Your readiness profile, singles journal, and daily reflections will be archived and accessible in settings. Recommended for future reference.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Import Insights */}
                {partnerFromJournal && (
                  <button
                    onClick={() => setImportInsights(!importInsights)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      importInsights
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-neutral-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        importInsights
                          ? 'border-blue-600 bg-blue-600'
                          : 'border-neutral-300'
                      }`}>
                        {importInsights && <CheckCircle2 className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">Import Singles Insights</p>
                        <p className="text-sm text-neutral-600 mt-1">
                          Import green/red flags and connection patterns from your {partnerFromJournal.stats.totalDates} date{partnerFromJournal.stats.totalDates !== 1 ? 's' : ''} with {partnerName} to inform your relationship tracking.
                        </p>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </Card>

            {/* Preview Transition */}
            <Card className="p-6 space-y-4 bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-violet-600" />
                <div>
                  <h3 className="font-medium text-violet-900">What's Next in Relationship Mode</h3>
                  <p className="text-sm text-violet-700">Your new journey begins</p>
                </div>
              </div>

              <div className="space-y-3 text-sm text-violet-900">
                <div className="flex items-start gap-3 bg-white p-3 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Daily Check-ins</p>
                    <p className="text-violet-700">Track tension dynamics in your relationship with {partnerName}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-white p-3 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Tension Scoring (RTI)</p>
                    <p className="text-violet-700">Monitor your 5-component Relationship Tension Index</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-white p-3 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Personalized Actions</p>
                    <p className="text-violet-700">Get guidance based on your tension state and patterns</p>
                  </div>
                </div>

                {importInsights && partnerFromJournal && (
                  <div className="flex items-start gap-3 bg-white p-3 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Imported Context</p>
                      <p className="text-violet-700">Your singles history will inform early relationship insights</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Summary */}
            <Card className="p-6 space-y-3">
              <h3 className="font-medium text-neutral-900">Transition Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-neutral-200">
                  <span className="text-neutral-600">Partner Name</span>
                  <span className="font-medium text-neutral-900">{partnerName}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-neutral-200">
                  <span className="text-neutral-600">Relationship Start</span>
                  <span className="font-medium text-neutral-900">
                    {new Date(relationshipStart).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                {partnerFromJournal && (
                  <div className="flex justify-between py-2 border-b border-neutral-200">
                    <span className="text-neutral-600">Singles History</span>
                    <span className="font-medium text-neutral-900">{partnerFromJournal.stats.totalDates} dates tracked</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b border-neutral-200">
                  <span className="text-neutral-600">Singles Archive</span>
                  <span className="font-medium text-neutral-900">{keepSinglesData ? 'Preserved' : 'Not saved'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-neutral-600">Import Insights</span>
                  <span className="font-medium text-neutral-900">
                    {importInsights && partnerFromJournal ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </Card>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={completeTransition}
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                size="lg"
              >
                Complete Transition
                <Heart className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
